import { useId, useRef, useState } from 'react';

import { useTheme } from 'styled-components';

import { IFCWithChildren } from '~/common/interfaces/IFCWithChildren';
import { IAboveComponentDefaultProps } from '~/common/interfaces/IAboveComponentDefaultProps';
import { useAboveElementPossiblePosition } from '~/common/hooks/use-above-element-possible-position';
import { Typography } from '~/common/components/Typography';
import { Icon } from '~/common/components/Icons';

import {
  CloseWrapper,
  ContentWrapper,
  IconWrapper,
  StructuredTooltipContainer,
} from './styles';
import { AboveContainer } from '../styles';
import { AboveBox } from '../../Box';

interface IStructuredTooltipProps extends IAboveComponentDefaultProps {
  title: string;
  message: string;
  icon: string;
}

export const StructuredTooltip: IFCWithChildren<IStructuredTooltipProps> = ({
  children,
  icon,
  message,
  title,
  arrowPosition = 'center',
  background = 'neutral.50',
  orientation = 'vertical',
}) => {
  const theme = useTheme();
  const tooltipId = useId();
  const [visible, setVisible] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const position = useAboveElementPossiblePosition({
    orientation,
    visible,
    containerRef,
    boxId: tooltipId,
  });

  return (
    <AboveContainer
      ref={containerRef}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      popover={false}
    >
      <AboveBox
        id={tooltipId}
        visible={visible}
        position={position}
        arrowPosition={arrowPosition}
        background={background}
      >
        <StructuredTooltipContainer>
          <IconWrapper>
            <Icon
              type={icon}
              size={20}
              color={
                background === 'neutral.50'
                  ? theme.neutral[600]
                  : theme.neutral[400]
              }
            />
          </IconWrapper>
          <ContentWrapper>
            <Typography.Label
              color={background === 'neutral.50' ? 'neutral.950' : 'neutral.50'}
              size="small"
            >
              {title}
            </Typography.Label>
            <Typography.Paragraph
              size="x-small"
              color={
                background === 'neutral.50' ? 'neutral.600' : 'neutral.400'
              }
            >
              {message}
            </Typography.Paragraph>
          </ContentWrapper>
          <CloseWrapper>
            <Icon
              type="close-line"
              size={20}
              color={
                background === 'neutral.50'
                  ? theme.neutral[600]
                  : theme.neutral[400]
              }
            />
          </CloseWrapper>
        </StructuredTooltipContainer>
      </AboveBox>
      {children}
    </AboveContainer>
  );
};
